<template>
  <!-- ═══ INLINE OPT-IN (placed right after the heatmap) ═══ -->
  <section v-if="canDeliver" ref="optinRef" class="optin-section">
    <div class="optin-card" :class="mode">

      <!-- ── LEAD MODE: capture phone / Messenger ── -->
      <template v-if="mode === 'lead'">
        <div v-if="status !== 'success'" class="optin-body">
          <div class="optin-icon">📄</div>
          <h3 v-if="heading" class="optin-title" v-html="heading"></h3>
          <h3 v-else class="optin-title">এই গাইডলাইনের প্রিন্টযোগ্য <span class="accent">PDF</span> ফ্রি নিন</h3>
          <p class="optin-sub">
            {{ subText || 'মোবাইল নম্বর দিন — সাথে সাথে ডাউনলোড লিংক পাবেন। অথবা Messenger-এ নিন।' }}
          </p>

          <form class="optin-form" @submit.prevent="submitPhone">
            <input
              v-model="phone"
              type="tel"
              inputmode="numeric"
              class="optin-input"
              :class="{ invalid: status === 'error' }"
              placeholder="আপনার মোবাইল নম্বর (01XXXXXXXXX)"
              maxlength="14"
              :disabled="status === 'submitting'"
            />
            <button type="submit" class="optin-cta" :disabled="status === 'submitting'">
              {{ status === 'submitting' ? 'অপেক্ষা করুন…' : ctaLabel }}
            </button>
          </form>

          <p v-if="status === 'error'" class="optin-err">{{ errorMsg }}</p>

          <div v-if="messengerEnabled" class="optin-or"><span>অথবা</span></div>

          <a v-if="messengerEnabled" :href="messengerHref" target="_blank" rel="noopener" class="optin-messenger" @click="onMessenger">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M12 2C6.21 2 1.6 6.37 1.6 12.05c0 3.18 1.45 6.02 3.72 7.86V24l3.4-1.87c.9.25 1.86.39 2.86.39 5.79 0 10.4-4.37 10.4-10.05S17.79 2 12 2zm1.05 13.5l-2.65-2.83-5.17 2.83 5.69-6.04 2.72 2.83 5.1-2.83-5.69 6.04z"/>
            </svg>
            Messenger-এ PDF নিন
          </a>

          <p class="optin-consent">
            আপনার নম্বর শুধু গাইডলাইন ও আপডেট পাঠাতে ব্যবহৃত হবে।
            <a href="/privacy-policy" target="_blank" rel="noopener">গোপনীয়তা নীতি</a>।
          </p>
        </div>

        <!-- success -->
        <div v-else class="optin-success">
          <div class="optin-icon">✅</div>
          <h3 class="optin-title">ধন্যবাদ! আপনার PDF প্রস্তুত।</h3>
          <a v-if="pdf" :href="pdf" :download="downloadName" class="optin-cta wide" @click="onPdfDownload">
            {{ downloadBtnLabel }}
          </a>
          <p v-else class="optin-sub">
            আপনার PDF টি Messenger-এ পাঠানো হচ্ছে — ইনবক্স চেক করুন।
          </p>
          <a
            href="https://play.google.com/store/apps/details?id=com.bcsprostuti.tanim.bcsprostuti"
            target="_blank" rel="noopener" class="optin-applink" @click="onAppInstall"
          >
            ফ্রি অ্যাপ ইনস্টল করে সম্পূর্ণ প্রস্তুতি নিন →
          </a>
        </div>
      </template>

      <!-- ── MEMBER MODE: owned user, no capture ── -->
      <template v-else>
        <div class="optin-body">
          <div class="optin-icon">🎉</div>
          <h3 class="optin-title">আপনি তো বিসিএস প্রস্তুতি পরিবারের সদস্য!</h3>
          <p class="optin-sub">সরাসরি এই গাইডলাইনের PDF ডাউনলোড করুন।</p>
          <div class="optin-member-actions">
            <a v-if="pdf" :href="pdf" :download="downloadName" class="optin-cta" @click="onPdfDownload">
              {{ downloadBtnLabel }}
            </a>
            <a v-else :href="messengerHref" target="_blank" rel="noopener" class="optin-cta" @click="onMessenger">
              Messenger-এ PDF নিন
            </a>
            <a href="/packages" class="optin-pkg" @click="onPackage">প্যাকেজ দেখুন →</a>
          </div>
        </div>
      </template>
    </div>
  </section>

  <!-- ═══ DISMISSIBLE MOBILE STICKY BAR (lead mode only) ═══ -->
  <transition name="sticky-slide">
    <div
      v-if="canDeliver && mode === 'lead' && status !== 'success' && !stickyDismissed"
      class="optin-sticky"
    >
      <button class="sticky-cta" @click="scrollToOptin">{{ stickyLabel || '📄 ফ্রি গাইডলাইন PDF নিন' }}</button>
      <button class="sticky-close" aria-label="বন্ধ করুন" @click="stickyDismissed = true">✕</button>
    </div>
  </transition>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { getEntryMode } from "@/config/attribution";
import { saveLead } from "@/config/leads";
import {
  trackLead,
  trackAppInstallClick,
  trackInitiateCheckout,
} from "@/config/pixels";

export default {
  name: "GuidelineOptin",
  props: {
    // guideline key: bangla | english | bangladesh | ict | (course key, e.g. primary_headmaster)
    subject: { type: String, required: true },
    // optional path to the printable PDF in /public (omit if not produced yet)
    pdf: { type: String, default: "" },
    // optional copy overrides — let the same opt-in serve routine/course downloads
    heading: { type: String, default: "" },      // v-html; falls back to guideline copy
    subText: { type: String, default: "" },
    downloadLabel: { type: String, default: "" }, // overrides the "ডাউনলোড করুন" button text
    stickyLabel: { type: String, default: "" },
    fileName: { type: String, default: "" },      // overrides the download filename
  },
  setup(props) {
    const optinRef = ref(null);
    const phone = ref("");
    const status = ref("idle"); // idle | submitting | success | error
    const errorMsg = ref("");
    const stickyDismissed = ref(false);
    const mode = ref("lead");

    // Use the canonical FB Page ID (m.me resolves it reliably). Swap to a
    // vanity handle here if the page later gets one. ref= carries the subject
    // into ManyChat so it can auto-send the matching PDF.
    const messengerHref = computed(
      () => `https://m.me/500734590359140?ref=${props.subject}_guideline`
    );
    const downloadName = computed(
      () => props.fileName || `bcs_${props.subject}_guideline.pdf`
    );
    const ctaLabel = computed(() => props.downloadLabel || "ফ্রি PDF ডাউনলোড করুন");
    const downloadBtnLabel = computed(() => props.downloadLabel || "PDF ডাউনলোড করুন");

    // Messenger delivery is OFF until ManyChat is live. Flip to true to bring
    // back the Messenger button (and enable PDF-less pages like Bangladesh/ICT
    // to deliver via Messenger). Until then, the opt-in only shows where a
    // downloadable PDF exists, so users never hit a dead end.
    const MESSENGER_ENABLED = false;
    const canDeliver = computed(() => !!props.pdf || MESSENGER_ENABLED);

    const submitPhone = async () => {
      if (status.value === "submitting") return;
      status.value = "submitting";
      errorMsg.value = "";

      const res = await saveLead({
        phone: phone.value,
        subject: props.subject,
        mode: mode.value,
      });

      if (res.ok) {
        status.value = "success";
        // res.phone → Meta Advanced Matching (hashed client-side by
        // fbevents.js). res.eventId → dedup key shared with the server-side
        // Conversions API Lead event.
        trackLead(props.subject, "phone", res.phone, res.eventId);
      } else {
        status.value = "error";
        errorMsg.value =
          res.error === "invalid_phone"
            ? "সঠিক মোবাইল নম্বর দিন (যেমন 01712345678)।"
            : "কিছু একটা সমস্যা হয়েছে, আবার চেষ্টা করুন।";
      }
    };

    const onMessenger = () => trackLead(props.subject, "messenger");
    const onAppInstall = () => trackAppInstallClick(props.subject);
    const onPackage = () => trackInitiateCheckout(props.subject);
    const onPdfDownload = () => {
      /* delivery confirmed; Lead already fired on opt-in */
    };

    const scrollToOptin = () => {
      if (optinRef.value) {
        optinRef.value.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = optinRef.value.querySelector(".optin-input");
        if (input) setTimeout(() => input.focus(), 400);
      }
    };

    onMounted(() => {
      mode.value = getEntryMode();
    });

    return {
      optinRef, phone, status, errorMsg, stickyDismissed, mode,
      messengerHref, downloadName, ctaLabel, downloadBtnLabel,
      canDeliver, messengerEnabled: MESSENGER_ENABLED,
      submitPhone, onMessenger, onAppInstall, onPackage, onPdfDownload,
      scrollToOptin,
    };
  },
};
</script>

<style scoped>
/* Blue brand accents (#0095FF) + one warm high-contrast CTA (decision #7) */
.optin-section {
  padding: 8px 16px 40px;
  display: flex;
  justify-content: center;
}
.optin-card {
  width: 100%;
  max-width: 620px;
  background: linear-gradient(160deg, #f0f9ff 0%, #ffffff 60%);
  border: 1.5px solid #bae0ff;
  border-radius: 18px;
  padding: 30px 28px;
  text-align: center;
  box-shadow: 0 8px 30px rgba(0, 149, 255, 0.12);
  font-family: 'Noto Serif Bengali', system-ui, sans-serif;
}
.optin-card.member {
  background: linear-gradient(160deg, #eff6ff 0%, #ffffff 60%);
}
.optin-icon { font-size: 2.2rem; line-height: 1; margin-bottom: 10px; }
.optin-title {
  font-size: 1.3rem; font-weight: 800; color: #0b2447;
  margin: 0 0 8px; line-height: 1.4;
}
.optin-title .accent { color: #0095FF; }
.optin-sub {
  font-size: 0.95rem; color: #475569; margin: 0 0 18px; line-height: 1.6;
}

.optin-form { display: flex; flex-direction: column; gap: 10px; }
.optin-input {
  width: 100%; padding: 13px 16px; font-size: 1rem;
  border: 1.5px solid #cbd5e1; border-radius: 10px;
  font-family: system-ui, sans-serif; outline: none; transition: border-color 0.15s;
}
.optin-input:focus { border-color: #0095FF; box-shadow: 0 0 0 3px rgba(0,149,255,0.15); }
.optin-input.invalid { border-color: #ef4444; }

/* The one warm CTA — high contrast against the blue card */
.optin-cta {
  display: inline-block; width: 100%;
  padding: 14px 20px; font-size: 1.05rem; font-weight: 800;
  color: #fff; background: linear-gradient(90deg, #F59E0B, #F97316);
  border: none; border-radius: 10px; cursor: pointer;
  font-family: 'Noto Serif Bengali', system-ui, sans-serif;
  box-shadow: 0 6px 18px rgba(249, 115, 22, 0.35);
  transition: transform 0.12s, box-shadow 0.12s;
  text-decoration: none;
}
.optin-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(249,115,22,0.45); }
.optin-cta:disabled { opacity: 0.7; cursor: default; transform: none; }
.optin-cta.wide { margin-top: 6px; }

.optin-err { color: #dc2626; font-size: 0.85rem; margin: 10px 0 0; }

.optin-or {
  position: relative; text-align: center; margin: 18px 0;
  color: #94a3b8; font-size: 0.85rem;
}
.optin-or::before {
  content: ""; position: absolute; top: 50%; left: 0; right: 0;
  height: 1px; background: #e2e8f0;
}
.optin-or span { position: relative; background: #fff; padding: 0 12px; }

.optin-messenger {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 12px 18px; font-size: 0.98rem; font-weight: 700;
  color: #fff; background: #0095FF; border-radius: 10px; text-decoration: none;
  transition: background 0.15s;
}
.optin-messenger:hover { background: #007ad6; }

.optin-consent {
  font-size: 0.78rem; color: #94a3b8; margin: 14px 0 0;
  font-family: system-ui, sans-serif; line-height: 1.5;
}
.optin-consent a { color: #0095FF; text-decoration: underline; }

.optin-success .optin-title { color: #166534; }
.optin-applink {
  display: inline-block; margin-top: 16px; font-size: 0.95rem; font-weight: 700;
  color: #0095FF; text-decoration: none;
}
.optin-applink:hover { text-decoration: underline; }

.optin-member-actions {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.optin-member-actions .optin-cta { max-width: 320px; }
.optin-pkg { font-size: 0.95rem; font-weight: 700; color: #0095FF; text-decoration: none; }
.optin-pkg:hover { text-decoration: underline; }

/* ── Mobile sticky bar ── */
.optin-sticky {
  position: fixed; bottom: 0; left: 0; right: 0; z-index: 1200;
  display: none; align-items: center; gap: 10px;
  padding: 10px 14px; background: #0b2447;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.25);
}
.sticky-cta {
  flex: 1; padding: 12px; font-size: 1rem; font-weight: 800; color: #fff;
  background: linear-gradient(90deg, #F59E0B, #F97316);
  border: none; border-radius: 10px; cursor: pointer;
  font-family: 'Noto Serif Bengali', system-ui, sans-serif;
}
.sticky-close {
  flex-shrink: 0; width: 38px; height: 38px; border-radius: 8px;
  background: rgba(255,255,255,0.12); color: #fff; border: none;
  font-size: 1rem; cursor: pointer;
}
@media (max-width: 768px) { .optin-sticky { display: flex; } }

.sticky-slide-enter-active, .sticky-slide-leave-active { transition: transform 0.25s ease; }
.sticky-slide-enter-from, .sticky-slide-leave-to { transform: translateY(100%); }

@media (max-width: 480px) {
  .optin-card { padding: 24px 18px; }
  .optin-title { font-size: 1.15rem; }
}
</style>